import { useState } from "react";
import { toast } from "sonner";
import { streamAI, api, API, downloadText } from "../lib/api";
import { SECTORS } from "../lib/sectors";
import { FormatSelector, FORMATS } from "./FormatSelector";
import { PhoneMockup, parseStories } from "./PhoneMockup";
import { EmailSubjectChecker, SubjectLibrary, extractSubjects, useSubjectFavorites } from "./EmailSubjectChecker";
import { TelegramButton } from "./TelegramButton";
import { Sparkles, Loader2, Copy, CalendarPlus, Download, Smartphone, ImagePlus } from "lucide-react";

function renderRich(text) {
  const html = text
    .replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')
    .replace(/\n/g, "<br/>");
  return { __html: html };
}

const btn = "flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-50";

export function AIGenerator({ kind, sector, title, subtitle, placeholder, onSaved }) {
  const s = SECTORS[sector];
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [format, setFormat] = useState("post");
  const [genFormat, setGenFormat] = useState("post");
  const [subjects, setSubjects] = useState([]);
  const [matched, setMatched] = useState(null);
  const [matching, setMatching] = useState(false);
  const [preview, setPreview] = useState(false);
  const [storyIdx, setStoryIdx] = useState(0);
  const [activeSubj, setActiveSubj] = useState(0);
  const fav = useSubjectFavorites();

  const generate = async () => {
    setLoading(true); setOutput(""); setSubjects([]); setMatched(null); setStoryIdx(0); setGenFormat(format);
    let full = "";
    try {
      await streamAI("/ai/ideas", { kind, input, format }, (t) => { full += t; setOutput((p) => p + t); });
      if (format === "email") setSubjects(extractSubjects(full));
    } catch (e) {
      toast.error("Errore nella generazione");
    } finally {
      setLoading(false);
    }
  };

  const changeSubject = (i, v) => {
    const old = subjects[i];
    if (old) setOutput((o) => o.replace(old, v));
    setSubjects((arr) => arr.map((x, k) => (k === i ? v : x)));
  };

  const matchImage = async () => {
    setMatching(true);
    try {
      const r = await api.post("/ai/image-from-text", { text: output.replace(/\*\*/g, ""), format: genFormat, sector }, { timeout: 180000 });
      setMatched(r.data);
      window.dispatchEvent(new CustomEvent("cs:images-updated", { detail: { sector } }));
      toast.success(`Immagine ${FORMATS.find((f) => f.id === genFormat).sub.split("·").pop().trim()} creata e salvata in galleria`);
    } catch { toast.error("Errore nella creazione dell'immagine"); }
    finally { setMatching(false); }
  };

  const copy = () => { navigator.clipboard.writeText(output.replace(/\*\*/g, "")); toast.success("Copiato negli appunti"); };

  const saveIdea = async () => {
    await api.post("/contents", {
      sector,
      title: `Idee AI (${FORMATS.find((f) => f.id === genFormat).label}) — ${title}`,
      body: output.replace(/\*\*/g, ""),
      type: genFormat === "story" ? "storia" : "post",
      status: "idea",
    });
    toast.success("Salvato tra le idee del calendario");
    onSaved && onSaved();
  };

  const stories = genFormat === "story" && output && !loading ? parseStories(output) : [];
  const imgUrl = matched ? `${API}/images/${matched.id}/file` : null;
  const ready = output && !loading;

  return (
    <div className="glass rounded-2xl p-5" data-testid={`ai-generator-${kind}`}>
      <div className="flex items-center gap-2.5 mb-1">
        <div className="h-8 w-8 rounded-lg grid place-items-center" style={{ background: s.soft }}>
          <Sparkles className="h-4 w-4" style={{ color: s.hex }} />
        </div>
        <h3 className="font-display font-bold">{title}</h3>
      </div>
      {subtitle && <p className="text-slate-400 text-sm mb-3">{subtitle}</p>}

      <FormatSelector value={format} onChange={setFormat} color={s.hex} testid={`ai-format-${kind}`} />

      {format === "email" && (
        <SubjectLibrary fav={fav} kind={kind} canInsert={genFormat === "email" && ready && subjects.length > 0}
          onInsert={(v) => { changeSubject(Math.min(activeSubj, subjects.length - 1), v); toast.success("Oggetto inserito nell'email"); }} />
      )}

      <div className="flex gap-2 mb-3">
        <input data-testid={`ai-input-${kind}`} value={input} onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder || "Aggiungi un tema o spunto (facoltativo)…"}
          className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none"
          style={{ borderColor: input ? s.border : undefined }}
          onKeyDown={(e) => e.key === "Enter" && !loading && generate()} />
        <button data-testid={`ai-generate-${kind}`} onClick={generate} disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-semibold text-sm disabled:opacity-60 transition-opacity"
          style={{ background: s.hex }}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Genera
        </button>
      </div>

      {(output || loading) && (
        <div className="rounded-xl bg-black/20 border border-white/5 p-4">
          {ready && (
            <div className="flex flex-wrap justify-end gap-2 mb-2">
              <button data-testid={`ai-match-image-${kind}`} onClick={matchImage} disabled={matching} className={btn} style={{ color: s.hex }}>
                {matching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                {matching ? "Creo immagine…" : "Crea immagine abbinata"}
              </button>
              {stories.length > 0 && (
                <button data-testid={`ai-story-preview-${kind}`} onClick={() => setPreview(true)} className={btn}>
                  <Smartphone className="h-3.5 w-3.5" /> Anteprima Story
                </button>
              )}
              <TelegramButton testid={`ai-telegram-${kind}`} label="Telegram"
                getPayload={async () => ({ text: output, imageId: matched?.id || "" })} />
              <button data-testid={`ai-save-${kind}`} onClick={saveIdea} className={btn}><CalendarPlus className="h-3.5 w-3.5" /> Salva idea</button>
              <button data-testid={`ai-export-${kind}`} onClick={() => downloadText(`idee-${sector}.md`, `# ${title}\n\n${output}`)} className={btn}><Download className="h-3.5 w-3.5" /> Esporta</button>
              <button data-testid={`ai-copy-${kind}`} onClick={copy} className={btn}><Copy className="h-3.5 w-3.5" /> Copia</button>
            </div>
          )}

          {genFormat === "email" && ready && (
            <EmailSubjectChecker subjects={subjects} onChange={changeSubject} fav={fav} sector={sector} active={activeSubj} setActive={setActiveSubj} />
          )}

          {matched && (
            <MatchedImage img={matched} url={imgUrl} kind={kind} onPreview={matched.format === "story" ? () => setPreview(true) : null} />
          )}

          {stories.length > 1 && (
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid={`ai-story-picker-${kind}`}>
              {stories.map((_, k) => (
                <button key={k} onClick={() => setStoryIdx(k)} data-testid={`ai-story-pick-${kind}-${k}`}
                  className="text-[11px] px-2 py-1 rounded-md border transition-colors"
                  style={{ borderColor: k === storyIdx ? s.hex : "rgba(255,255,255,0.08)", color: k === storyIdx ? s.hex : undefined }}>
                  Story {k + 1}
                </button>
              ))}
            </div>
          )}

          <div className="text-sm leading-relaxed text-slate-200 whitespace-pre-wrap max-h-80 overflow-y-auto"
            data-testid={`ai-output-${kind}`} dangerouslySetInnerHTML={renderRich(output || "…")} />
        </div>
      )}

      <PhoneMockup open={preview} onOpenChange={setPreview} color={s.hex} imageUrl={imgUrl} imageId={matched?.id || ""}
        slides={stories[storyIdx]?.slides || []} testid={`story-mockup-${kind}`} />
    </div>
  );
}

function MatchedImage({ img, url, kind, onPreview }) {
  const f = FORMATS.find((x) => x.id === img.format) || FORMATS[0];
  return (
    <div className="flex items-center gap-3 mb-3 p-2 rounded-xl bg-white/[0.03] border border-white/10" data-testid={`ai-matched-image-${kind}`}>
      <img src={url} alt="Immagine abbinata" className="h-20 rounded-lg object-cover" style={{ aspectRatio: f.aspect }} />
      <div className="min-w-0 text-xs">
        <p className="font-semibold">Immagine abbinata · {f.label}</p>
        <p className="text-slate-400">{img.width}×{img.height} · salvata nella galleria del settore</p>
        {onPreview && <button onClick={onPreview} className="mt-1 underline text-slate-200" data-testid={`ai-matched-preview-${kind}`}>Vedi nella Story</button>}
      </div>
    </div>
  );
}
